import classNames from "classnames"
import { useAppSelector } from "integrations/redux/hooks"
import { selectAncestorEntitiesByEntityId } from "panels/content/side-panel/slice"
import { selectColorFinderIsOpen } from "plugins/ColorFinder/V2/redux/selectors"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { getMapNodePlugins, MapNodeIcon, MapNodeIconState } from "plugins/plugin-registry"
import { selectCurrentGeometryToolType } from "plugins/shared/geometry/SharedGeometrySlice"
import { selectCommentPlacementState } from "features/entity-caching/map-comments/selectors"
import { useState } from "react"
import { NovaMarker } from "../NovaMarker"
import { Svg } from "themes/svg/Svg"

export function MapNodes() {
    const plugins = getMapNodePlugins()
    const mapNodes = plugins.flatMap((plugin) => plugin.useMapNodes())
    return (
        <>
            {mapNodes.map((node) => (
                <MapboxMarkerIcon key={node.id} node={node} />
            ))}
        </>
    )
}

export type MapNode = ReturnType<
    ReturnType<typeof getMapNodePlugins>[number]["useMapNodes"]
>[number]

const MapboxMarkerIcon = ({ node }: { node: MapNode }) => {
    const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(node.id))
    const someGeometryToolIsInUse = !!useAppSelector(selectCurrentGeometryToolType)
    const commentPlacementMode = useAppSelector(selectCommentPlacementState).mode
    const blockedByCommenting =
        commentPlacementMode !== "disabled" && !node.interactiveWhileCommenting
    const allowInteraction =
        !node.locked &&
        !ancestors.some((ancestor) => ancestor.locked) &&
        !someGeometryToolIsInUse &&
        !blockedByCommenting
    const [isHovering, setIsHovering] = useState(false)

    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())
    const colorfinderIsOpen = useAppSelector(selectColorFinderIsOpen)

    if (pdfSnapshotOpen || colorfinderIsOpen) return null
    if (!node.visible || ancestors.some((ancestor) => !ancestor.visible)) return null

    return (
        <NovaMarker
            data-map-node="true"
            lng={node.position.lng}
            lat={node.position.lat}
            anchor={node.anchor ?? "center"}
            className={classNames("w-fit rounded-xl", {
                "cursor-pointer pointer-events-auto": allowInteraction,
                "cursor-auto pointer-events-none": !allowInteraction,
            })}
            style={
                isHovering && allowInteraction
                    ? { boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px" }
                    : undefined
            }
            onClick={allowInteraction ? node.onClick : undefined}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            draggable={allowInteraction && node.draggable}
            onDragEnd={node.onDragEnd}
        >
            <MapNodeIconView
                icon={node.iconSrc}
                state={node.active ? "active" : isHovering ? "hover" : "default"}
            />
        </NovaMarker>
    )
}

const MapNodeIconView = ({ icon, state }: { icon: MapNodeIcon; state: MapNodeIconState }) => {
    if (icon.kind === "render") return <icon.Component state={state} />
    return <Svg src={icon.icons[state] ?? icon.icons.default} />
}
